import React, { createContext, useContext, useEffect, useRef } from 'react';
import { createStore, StoreApi } from 'zustand';
import devtools from './devtools';

export interface Location {
  pathname: string;
  search: string;
}

export interface RouterValues {
  location: Location;
}

export interface RouterActions {
  navigate: (to: string) => void;
}

export interface RouterState extends RouterValues, RouterActions {}

const defaultState = {} as RouterValues;

const getLocationValues = <T extends Location>(l: T) => ({ pathname: l.pathname, search: l.search });

export const createRouterState = (init: { url?: string }) =>
  createStore<RouterState>()(
    devtools((set) => {
      if (typeof window === 'undefined') {
        if (!init.url) throw new Error('url is required');

        const url = new URL(init.url, 'https://example.com');
        return {
          ...defaultState,
          location: getLocationValues(url),
          navigate: () => {},
        };
      }

      return {
        ...defaultState,
        location: getLocationValues(window.location),
        navigate: (to) => {
          window.history.pushState(undefined, '', to);
          set({ location: getLocationValues(window.location) }, false, 'router/navigate');
        },
      };
    }),
  );

const RouterContext = createContext<StoreApi<RouterState> | null>(null);

// eslint-disable-next-line react/function-component-definition
export const Router: React.FC<React.PropsWithChildren<{ url?: string }>> = ({ children, ...init }) => {
  const storeRef = useRef<ReturnType<typeof createRouterState>>();
  if (!storeRef.current) storeRef.current = createRouterState(init);

  useEffect(() => {
    const handlePopState = () => {
      storeRef.current?.setState({
        location: getLocationValues(window.location),
      });
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return <RouterContext.Provider value={storeRef.current}>{children}</RouterContext.Provider>;
};

export const useRouterStore = () => {
  const store = useContext(RouterContext);
  if (!store) throw new Error('Missing Router Context Provider');

  return store;
};
